import {useCallback} from 'react';

import {
  type Account,
  AccountPlanType,
  AccountType,
} from '../../components/Search/SearchNav/types';
import {
  UsageType,
  useBillingPeriodSubscriptionsQuery,
} from '../../generated/graphql';
import {
  getWbagentSubBillingPeriod,
  useAccountPrivileges,
} from '../accounts/pricing';
import {useRampFlagEnableWbAgentUsageCharts} from '../rampFeatureFlags/rampFeatureFlags';
import {
  getIntervalMarkersFromCurrentCalendarMonth,
  getIntervalMarkersFromLimitDateInterval,
  getIntervalMarkersFromSubscription,
  useGetUsageThisBillingPeriod,
} from './utils';

export function isWbAgentBillingPlanEligible(account: Account): boolean {
  return (
    account.accountPlanType === AccountPlanType.Pro ||
    account.accountPlanType === AccountPlanType.Enterprise
  );
}

/** Call only when `orgName` is known (e.g. from the URL or account selector). */
export function useIsWbAgentBillingUsageVisible(orgName: string): {
  visible: boolean;
  loading: boolean;
} {
  const {enabled, loading} = useRampFlagEnableWbAgentUsageCharts(orgName);
  return {
    visible: !loading && enabled,
    loading,
  };
}

function useWbAgentBillingUsageVisibility(orgName: string | null): {
  visible: boolean;
  loading: boolean;
} {
  const hasOrg = orgName != null;

  // When there is no org, pass null so useOrgNameRampFlag skips the query
  // (its skip condition is `orgName == null`). Treat the flag as false.
  const {enabled, loading: rampLoading} = useRampFlagEnableWbAgentUsageCharts(
    (hasOrg ? orgName : null) as string
  );

  if (!hasOrg) {
    return {visible: false, loading: false};
  }

  return {
    visible: !rampLoading && enabled,
    loading: rampLoading,
  };
}

/**
 * Hook to get WB Agent usage details for an account.
 *
 * NOTE: This hook is for multi-tenant (SaaS) deployments only. Do NOT use in
 * single-tenant (server) deployments. For server deployments, use the hooks in
 * ServerCurrentBillingPeriodUsageSection.tsx.
 */
export function useGetWbagentUsageDetails(selectedAccount: Account | null) {
  const orgName =
    selectedAccount?.accountType === AccountType.Personal
      ? null
      : (selectedAccount?.name ?? null);
  const {visible: isRampVisible, loading: visibilityLoading} =
    useWbAgentBillingUsageVisibility(orgName);
  const privilegeResults = useAccountPrivileges(selectedAccount);
  const {privileges, refetch: privilegesRefetch} = privilegeResults;
  const {wbagentLimits} = privileges;

  // isUsagePaid marks whether pay-as-you-go is enabled, not whether the
  // account pays. Hardcoded false until the real value is wired up.
  const isUsagePaid = false;

  const isEligible =
    selectedAccount != null && isWbAgentBillingPlanEligible(selectedAccount);
  const isVisible = isRampVisible && isEligible;
  // Fetch is broader than isVisible so allotment-bearing Academic/Free orgs
  // (not just eligible plans) get metered.
  const shouldTrackUsage =
    isEligible || wbagentLimits.wbagentFreeLimit > 0 || isUsagePaid;
  const skip =
    selectedAccount?.name == null ||
    visibilityLoading ||
    !isRampVisible ||
    !shouldTrackUsage;

  const billingPeriodResults = useBillingPeriodSubscriptionsQuery({
    variables: {orgName: skip ? '' : selectedAccount!.name},
    skip,
  });

  const organization = billingPeriodResults.data?.organization ?? undefined;

  // TODO: Once Orb integration is added, prefer enterprise subscription billing
  // period over privileges (see inference.ts finalLimitDateInterval pattern).
  const usagePeriod = selectedAccount?.isEnterprise
    ? getIntervalMarkersFromLimitDateInterval(
        privileges.wbagentLimitDateInterval
      )
    : (getIntervalMarkersFromSubscription(
        getWbagentSubBillingPeriod(
          selectedAccount?.accountPlanType,
          organization
        )
      ) ?? getIntervalMarkersFromCurrentCalendarMonth());

  const usageResults = useGetUsageThisBillingPeriod(
    selectedAccount,
    UsageType.WbAgentCost,
    usagePeriod,
    skip
  );

  const loading =
    visibilityLoading ||
    billingPeriodResults.loading ||
    privilegeResults.loading ||
    usageResults.loading;
  const error =
    billingPeriodResults.error ?? privilegeResults.error ?? usageResults.error;
  const [billingPeriodStart, billingPeriodEnd] = usagePeriod;
  const {usage, refetch: usageRefetch} = usageResults;
  const {refetch: billingPeriodRefetch} = billingPeriodResults;

  const refetch = useCallback(async (): Promise<void> => {
    // Skip-guarded because Apollo refetch fires even when the query is skipped.
    if (skip) {
      return;
    }

    // Only usage changes often, so refetch just that, plus any other query
    // still in error.
    await Promise.allSettled([
      usageRefetch(),
      billingPeriodResults.error != null ? billingPeriodRefetch() : null,
      privilegeResults.error != null ? privilegesRefetch() : null,
    ]);
  }, [
    skip,
    usageRefetch,
    billingPeriodRefetch,
    privilegesRefetch,
    billingPeriodResults.error,
    privilegeResults.error,
  ]);

  return {
    isUsagePaid,
    wbagentLimits,
    billingPeriodStart,
    billingPeriodEnd,
    usage,
    refetch,
    loading,
    error,
    isVisible,
    visibilityLoading,
    isFeatureEnabled: isRampVisible,
  };
}
